import React, { FC, Fragment, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Spinner } from '../Spinner';
import { Column } from 'react-table';
import {
  useTableFeatures,
  SelectableRowsProp,
  getRowBackgroundColor,
  getRowBorderColor,
} from './useTableFeatures';

export interface TableProps extends SelectableRowsProp {
  configuration: Column[];
  data: any[];
  isLoading?: boolean;
  testId: string;
  height?: string;
  isStickyHeader?: boolean;
  rowHeight?: string;
  renderSubRow?: (row: any) => ReactNode;
  className?: string;
}

export const Table: FC<TableProps> = ({
  configuration,
  data,
  testId,
  isLoading,
  height,
  isStickyHeader = true,
  rowHeight,
  selectableRows,
  renderSubRow,
  className,
}) => {
  const isExpandable = !!renderSubRow;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTableFeatures({ configuration, data, selectableRows, isExpandable });

  const tableTestId = `${testId}-table`;
  const rowTestId = `${tableTestId}-row`;
  const cellTestId = `${tableTestId}-cell`;
  const headerRowTestId = `${tableTestId}-header-row`;
  const headerCellTestId = `${tableTestId}-header-cell`;

  if (!data || !configuration) {
    return null;
  }

  return isLoading ? (
    <Spinner isCentered />
  ) : (
    <Container height={height}>
      <TableComponent
        {...getTableProps()}
        className={className}
        data-test-id={tableTestId}
        selectableRows={selectableRows}
        rowHeight={rowHeight}
      >
        <TableHead isStickyHeader={isStickyHeader} isExpandable={isExpandable}>
          {headerGroups.map((headerGroup, index) => (
            <HeaderRow
              {...headerGroup.getHeaderGroupProps()}
              data-test-id={`${headerRowTestId}-${index}`}
              key={`${headerRowTestId}-${index}`}
            >
              {headerGroup.headers.map((column, headerIndex) => (
                <TH
                  {...column.getHeaderProps()}
                  data-test-id={`${headerCellTestId}-${headerIndex}`}
                  key={`${headerCellTestId}-${headerIndex}`}
                >
                  {column.render('Header')}
                </TH>
              ))}
            </HeaderRow>
          ))}
        </TableHead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <Fragment key={`${rowTestId}-${rowIndex}`}>
                <TR
                  {...row.getRowProps()}
                  type={row.original.tableProps?.type}
                  isExpanded={row.isExpanded}
                  key={row.id || rowIndex}
                >
                  {row.cells.map((cell, index) => {
                    return (
                      <TD
                        {...cell.getCellProps()}
                        data-test-id={`${cellTestId}-${rowIndex}-${index}`}
                        type={row.original.tableProps?.type}
                        key={`${cellTestId}-${rowIndex}-${index}`}
                      >
                        {cell.render('Cell')}
                      </TD>
                    );
                  })}
                </TR>
                {row.isExpanded && (
                  <SubRow
                    {...row.getRowProps()}
                    type={row.original.tableProps?.type}
                    key={`${rowTestId}-${rowIndex}-subrow`}
                  >
                    <SubRowCell
                      colSpan={visibleColumns.length}
                      data-test-id={`${cellTestId}-${rowIndex}-subrow-cell`}
                    >
                      {renderSubRow(row)}
                    </SubRowCell>
                  </SubRow>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </TableComponent>
    </Container>
  );
};

const TableComponent = styled.table<{ selectableRows; rowHeight }>`
  border-spacing: 0;
  position: relative;
  min-width: 100%;
  th,
  td {
    position: relative;
    margin: 0;
    text-align: left;
    white-space: nowrap;
    height: ${({ rowHeight }) => rowHeight ?? 'auto'};
    padding: 1rem;
    :first-child {
      :after {
        opacity: ${({ selectableRows }) => (selectableRows ? 0 : 1)};
      }
    }
    :last-child {
      border-right: 0;
      &:after {
        content: none;
      }
    }
    :after {
      content: '';
      width: 1px;
      height: 50%;
      top: 25%;
      bottom: 25%;
      background-color: ${({ theme }) => theme.colors.primaryBorder};
      position: absolute;
      right: 0;
    }
  }
`;

const Container = styled.div<{ height: string }>`
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.primaryBorder};
  height: ${({ height }) => height ?? '37em'};
  overflow: auto;
`;
const TH = styled.th`
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueBorder};
`;
const TD = styled.td<{ type }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.primaryBorder};
  padding: 1rem;
  :first-child {
    padding-left: 1.5em;
    &: before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 5px;
      background-color: ${({ type, theme }) => getRowBorderColor(type, theme)};
    }
  }
`;
const TDNoBorderBottom = css`
  ${TD} {
    border-bottom: 0;
  }
`;
const TR = styled.tr<{ type; isExpanded: boolean }>`
  background: ${({ type, theme }) => getRowBackgroundColor(type, theme)};
  ${({ isExpanded }) => isExpanded && TDNoBorderBottom};
  :last-child {
    ${TDNoBorderBottom}
  }
`;
const SubRowCell = styled.td`
  &&& {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primaryBorder};
    padding: 0;
  }
`;
const SubRow = styled(TR)`
  :last-child {
    ${SubRowCell} {
      border-bottom: 0;
    }
  }
`;
const HeaderRow = styled(TR)`
  position: relative;
  z-index: 1;
  background: ${({ theme }) => theme?.colors?.white};
  box-shadow: 4px 4px 6px rgba(0, 0, 0, 0.2);
  ${TH} {
    :first-child {
      padding-left: 1.5em;
    }
  }
`;
const TableHead = styled.thead<{
  isStickyHeader: boolean;
  isExpandable: boolean;
}>`
  position: ${({ isStickyHeader }) =>
    (isStickyHeader && 'sticky') || 'relative'};
  background-color: ${({ theme }) => theme.colors.white};
  top: 0;
  z-index: ${({ isExpandable }) => (isExpandable && 10) || 1};
`;
